import AtriumApi from "api";
import HeatMapChart from "components/charts/HeatMapChart";
import Loader from "components/loader/Loader";
import { useEffect, useState } from "react";

const chartOptions = {
  chart: {
    toolbar: {
      show: false,
    },
    dropShadow: {
      enabled: false,
    },
  },
  tooltip: {
    enabled: false,
    // theme: "dark",
  },
  stroke: {
    show: true,
    type: "line",
    width: 2,
    colors: ["#FFFFFF"],
  },
  plotOptions: {
    heatmap: {
      useFillColorAsStroke: false,
      radius: 5,
      enableShades: false,
      colorScale: {
        ranges: [
          {
            from: 0,
            to: 50,
            name: "Low",
            color: "#E6F2E3",
            foreColor: "#6DA38C",
          },
          {
            from: 51,
            to: 100,
            name: "Medium",
            color: "#B4D6B9",
            foreColor: "#FFFFFF",
          },
          {
            from: 101,
            to: 150,
            name: "High",
            color: "#6DA38C",
            foreColor: "#FFFFFF",
          },
          {
            from: 151,
            to: 100000,
            name: "Extreme",
            color: "#3F6A58",
            foreColor: "#FFFFFF",
          },
        ],
      },
    },
  },
  dataLabels: {
    enabled: true,
    distributed: false,
    dropShadow: {
      enabled: false,
    },
  },
  xaxis: {
    // type: "numeric",
    // categories: ["SEP", "OCT", "NOV", "DEC", "JAN", "FEB"],
    labels: {
      style: {
        colors: "#A3AED0",
        fontSize: "12px",
        fontWeight: "500",
      },
    },
    axisBorder: {
      show: false,
    },
    axisTicks: {
      show: false,
    },
  },
  yaxis: {
    // type: "numeric",
    labels: {
      style: {
        colors: "#A3AED0",
        fontSize: "12px",
        fontWeight: "500",
      },
    },
  },
  legend: {
    show: false,
  },
  grid: {
    show: false,
  },
  states: {
    hover: { filter: "none" },
    active: { filter: "none" },
  },
  fill: {
    opacity: 1,
  },
};

const staticData = [
  {
    name: "8AM",
    data: [
      { x: "Mon", y: 0 },
      { x: "Tue", y: 0 },
      { x: "Wed", y: 0 },
      { x: "Thu", y: 0 },
      { x: "Fri", y: 0 },
      { x: "Sat", y: 0 },
      { x: "Sun", y: 0 },
    ],
  },
  {
    name: "10AM",
    data: [
      { x: "Mon", y: 0 },
      { x: "Tue", y: 0 },
      { x: "Wed", y: 0 },
      { x: "Thu", y: 0 },
      { x: "Fri", y: 0 },
      { x: "Sat", y: 0 },
      { x: "Sun", y: 0 },
    ],
  },
  {
    name: "12AM",
    data: [
      { x: "Mon", y: 0 },
      { x: "Tue", y: 0 },
      { x: "Wed", y: 0 },
      { x: "Thu", y: 0 },
      { x: "Fri", y: 0 },
      { x: "Sat", y: 0 },
      { x: "Sun", y: 0 },
    ],
  },
  {
    name: "2PM",
    data: [
      { x: "Mon", y: 0 },
      { x: "Tue", y: 0 },
      { x: "Wed", y: 0 },
      { x: "Thu", y: 0 },
      { x: "Fri", y: 0 },
      { x: "Sat", y: 0 },
      { x: "Sun", y: 0 },
    ],
  },
  {
    name: "4PM",
    data: [
      { x: "Mon", y: 0 },
      { x: "Tue", y: 0 },
      { x: "Wed", y: 0 },
      { x: "Thu", y: 0 },
      { x: "Fri", y: 0 },
      { x: "Sat", y: 0 },
      { x: "Sun", y: 0 },
    ],
  },
  {
    name: "6PM",
    data: [
      { x: "Mon", y: 0 },
      { x: "Tue", y: 0 },
      { x: "Wed", y: 0 },
      { x: "Thu", y: 0 },
      { x: "Fri", y: 0 },
      { x: "Sat", y: 0 },
      { x: "Sun", y: 0 },
    ],
  },
];

function BusiestTime({ selectedCampusId }) {
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busiestHour, setBusiestHour] = useState("");

  const newOptions = {
    ...chartOptions,
    colors: ["#A7D5BA", "#EF9C8F"],
  };

  useEffect(() => {
    function buildQueryString() {
      let query = "";

      if (selectedCampusId && selectedCampusId !== "all") {
        query += `${query ? "&" : ""}campusId=${selectedCampusId}`;
      }
      return query;
    }

    const getBusiestTime = async () => {
      try {
        setLoading(true);
        const query = buildQueryString();
        const response = await AtriumApi.get(
          `/v1/busy-time${query ? `?${query}` : ""}`
        );
        if (response) {
          setLoading(false);
          let finalData = [];

          const isNoDevice = response?.data?.message === "No device found";
          const rawData = response?.data?.data;

          if (isNoDevice || !rawData || rawData?.length === 0) {
            finalData = staticData;
            setChartData(staticData);
            setBusiestHour("");
            return;
          }

          const filteredData = rawData?.filter((item) =>
            item?.data?.some((d) => d?.y !== 0)
          );
          finalData = filteredData?.length > 0 ? filteredData : staticData;

          // ✅ Only calculate busiestHour if NOT "No device found"
          let maxHour = "";
          let maxTotal = -1;
          let hasValidData = false;

          finalData.forEach((series) => {
            const total = series?.data?.reduce((sum, d) => sum + d?.y, 0);
            if (total > 0) hasValidData = true;
            if (total > maxTotal) {
              maxTotal = total;
              maxHour = series.name;
            }
          });

          // setBusiestHour(maxHour || "");
          setBusiestHour(hasValidData ? maxHour : "");
          setChartData(finalData);
        }
      } catch (error) {
        setLoading(false);
        console.log("error while fetching busiest time", error);
      }
    };

    getBusiestTime();
  }, [selectedCampusId]);

  return (
    <>
      <div className="flex-1 relative items-center rounded-[30px] p-[30px] gap-[30px] shadow-atrium shadow-shadow-500 dark:!bg-navy-800 dark:shadow-none">
        {loading ? (
          <div className="flex h-full w-full items-center justify-center">
            <Loader />
          </div>
        ) : (
          <>
            {/* header */}
            <div className="flex">
              <div className="flex-1">
                <p className="text-gray-700">Busiest time of a day</p>
              </div>
              <div>
                {/* <select className="bg-[transparent] h-12 text-sm text-gray-700 rounded-xl pr-4 outline-none rounded-2xl p-[8px] mr-4">
                  <option>This year</option>
                </select> */}
                <p className="bg-[transparent] h-12 text-2xl font-bold rounded-xl pr-4 outline-none rounded-2xl p-[8px] mr-4">
                  {busiestHour}
                </p>
              </div>
            </div>

            {/* chart */}
            <div className="block h-[300px]">
              <div className="h-full w-full">
                {chartData?.length > 0 ? (
                  <HeatMapChart
                    chartData={chartData}
                    chartOptions={newOptions}
                  />
                ) : null}
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
}

export default BusiestTime;
